'use client'

import * as React from 'react'

const SIDEBAR_COOKIE_NAME = 'sidebar_state'
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = '16rem'
const SIDEBAR_WIDTH_MOBILE = '18rem'
const SIDEBAR_WIDTH_ICON = '3rem'
const SIDEBAR_KEYBOARD_SHORTCUT = 'b'

const SIDEBAR_REGISTRY = Symbol('sidebar-registry')
const SIDEBAR_TOKEN = Symbol('sidebar-token')

/**
 * Tailwind container-scale tiers (the same scale used by `@sm/main:`-style container variants),
 * resolved from the main content width rather than the viewport. Note the values differ from the
 * viewport breakpoints: `md` is 28rem here, not 48rem.
 */
type ContentBreakpoint =
  | '2xl'
  | '2xs'
  | '3xl'
  | '3xs'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'lg'
  | 'md'
  | 'sm'
  | 'xl'
  | 'xs'

type SidebarContextProps = {
  getSidebarState: (_target: SidebarToken) => SidebarState
  registerSidebar: (_target: SidebarToken, _registration: SidebarRegistration) => () => void
}

type SidebarControlledState<Token extends SidebarToken = SidebarToken> = {
  onOpenChange: (_open: boolean) => void
  open: boolean
  target: Token
}

type SidebarRegistration = {
  side: 'left' | 'right'
  width: string
}

type SidebarRegistry<
  Entries extends Record<string, SidebarRegistryEntry> = Record<string, SidebarRegistryEntry>,
> = {
  readonly [Name in keyof Entries]: SidebarToken<Extract<Name, string>>
} & {
  readonly [SIDEBAR_REGISTRY]: symbol
}

type SidebarRegistryEntry = {
  defaultOpen?: boolean
}

type SidebarRegistryToken<Registry extends SidebarRegistry> = Registry[keyof Registry &
  string] extends SidebarToken
  ? Registry[keyof Registry & string]
  : SidebarToken

/** Lets users drag or arrow-key a sidebar's inner edge; widths are in pixels. */
type SidebarResize = {
  maxWidth: number
  minWidth: number
  onResize: (_width: number) => void
}

type SidebarSetOpenOptions = {
  persist: boolean
}

type SidebarState = {
  isMobile: boolean
  open: boolean
  openMobile: boolean
  setOpen: (_open: boolean, _options?: SidebarSetOpenOptions) => void
  setOpenMobile: (_open: boolean) => void
  stackOffset: string
  state: 'collapsed' | 'expanded'
  toggleSidebar: () => void
}

type SidebarToken<Name extends string = string> = {
  readonly name: Name
  readonly [SIDEBAR_TOKEN]: {
    readonly defaultOpen: boolean
    readonly registry: symbol
  }
}

const DEFAULT_SIDEBAR_REGISTRY = Symbol('default-sidebar-registry')
const DEFAULT_SIDEBAR = Object.freeze({
  name: 'default',
  [SIDEBAR_TOKEN]: {
    defaultOpen: true,
    registry: DEFAULT_SIDEBAR_REGISTRY,
  },
}) satisfies SidebarToken<'default'>

const SidebarContext = React.createContext<null | SidebarContextProps>(null)
const SidebarInstanceContext = React.createContext<null | SidebarToken>(null)
const ContentBreakpointContext = React.createContext<ContentBreakpoint | null>(null)

/**
 * Creates typed sidebar tokens. Keep the returned registry stable for the lifetime of every
 * SidebarProvider that uses it, preferably by declaring it at module scope.
 */
function createSidebarRegistry<const Entries extends Record<string, SidebarRegistryEntry>>(
  entries: ('default' extends keyof Entries ? { default?: never } : unknown) & Entries,
): SidebarRegistry<Entries> {
  if (Object.prototype.hasOwnProperty.call(entries, DEFAULT_SIDEBAR.name)) {
    throw new Error(
      `Sidebar name "${DEFAULT_SIDEBAR.name}" is reserved for the implicit default sidebar.`,
    )
  }

  const registryId = Symbol('sidebar-registry-instance')
  const registry = Object.fromEntries(
    Object.entries(entries).map(([name, entry]) => [
      name,
      Object.freeze({
        name,
        [SIDEBAR_TOKEN]: {
          defaultOpen: entry.defaultOpen ?? false,
          registry: registryId,
        },
      }),
    ]),
  )

  Object.defineProperty(registry, SIDEBAR_REGISTRY, {
    enumerable: false,
    value: registryId,
  })

  // SAFETY: `registry` holds one token per key of `entries`, each named after its key, plus the
  // SIDEBAR_REGISTRY id defined above, which is the SidebarRegistry<Entries> contract.
  return Object.freeze(registry) as SidebarRegistry<Entries>
}

function persistDefaultSidebarState(open: boolean) {
  document.cookie = `${SIDEBAR_COOKIE_NAME}=${open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
}

export {
  ContentBreakpointContext,
  createSidebarRegistry,
  DEFAULT_SIDEBAR,
  persistDefaultSidebarState,
  SIDEBAR_KEYBOARD_SHORTCUT,
  SIDEBAR_REGISTRY,
  SIDEBAR_TOKEN,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_ICON,
  SIDEBAR_WIDTH_MOBILE,
  SidebarContext,
  SidebarInstanceContext,
}

export type {
  ContentBreakpoint,
  SidebarContextProps,
  SidebarControlledState,
  SidebarRegistration,
  SidebarRegistry,
  SidebarRegistryEntry,
  SidebarRegistryToken,
  SidebarResize,
  SidebarSetOpenOptions,
  SidebarState,
  SidebarToken,
}
